import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      {/* Title and content adjusted to avoid overlap with fixed navbar */}
      <h1>Privacy & Cookies Policy</h1>
      
      <section>
        <h2>Introduction</h2>
        <p>
          Where we refer to “process”, it means how we collect, use, store, make available, destroy, update, disclose, or otherwise deal with your personal information. As a rule, we will only process your personal information if this is required to deliver or offer a service, provide a product, or carry out a transaction.
        </p>
        <p>
          We may combine your personal information and use the combined personal information for any of the purposes stated in this Privacy Policy.
        </p>
        <p>
          In this document, any reference to “we” or “us” or “our” includes Lociware and any of its subsidiaries.
        </p>
        <p>
          If you use our services, goods, products, and service channels, you agree that we may process your personal information as explained under this Privacy Policy. Sometimes you may provide us with consent to process your personal information. Read it carefully because it may limit your rights.
        </p>
        <p>
          As a global organisation, this Privacy Policy will apply to the processing of personal information by any member of Lociware globally. If we process personal information for another party under a contract or a mandate, the other party’s privacy policy will apply to the processing.
        </p>
        <p>
          We can change this Privacy Policy from time to time if the law or our business practices require it.
        </p>
        <p>
          The version of the Privacy Policy displayed on our website will apply to your interactions with us.
        </p>
      </section>

      <section>
        <h2>What is Personal Information?</h2>
        <p>
          Personal information refers to any information that identifies you or specifically relates to you. Personal information includes, but is not limited to, the following information about you:
        </p>
        <ul className="policy-list">
          <li>Your marital status (like married, single, divorced).</li>
          <li>Your national origin.</li>
          <li>Your age.</li>
          <li>Your language; birth; education.</li>
          <li>Your financial history (like your income, third-party payments made on your behalf, and the like).</li>
          <li>Your identifying number (like an employee number, identity number, or passport number).</li>
          <li>Your e-mail address; physical address (like residential address, work address, or your physical location); telephone number.</li>
          <li>Your biometric information (like fingerprints, your signature, or voice).</li>
          <li>Your race; gender; sex; pregnancy; ethnic origin; social origin; colour; sexual orientation.</li>
          <li>Your physical health; mental health; well-being; disability; religion; belief; conscience; culture.</li>
          <li>Your medical history (like your HIV / AIDS status); criminal history; employment history.</li>
          <li>Your personal views, preferences, and opinions.</li>
          <li>Your confidential correspondence; and/or</li>
          <li>Another’s views or opinions about you and your name also constitute your personal information.</li>
        </ul>
        <p>
          Personal information includes special personal information, as explained below.
        </p>
      </section>

      <section>
        <h2>When Will We Process Your Personal Information?</h2>
        <p>
          We will only process your personal information for lawful purposes relating to our business if the following applies:
        </p>
        <ul className="policy-list">
          <li>If you have consented thereto.</li>
          <li>If a person legally authorised by you, the law, or a court, has consented thereto.</li>
          <li>If it is necessary to conclude or perform under a contract we have with you.</li>
          <li>If the law requires or permits it.</li>
          <li>If it is required to protect or pursue your, our, or a third party’s legitimate interest.</li>
        </ul>
      </section>

      <section>
        <h2>What is Special Personal Information?</h2>
        <p>
          Special personal information is personal information about the following:
        </p>
        <ul className="policy-list">
          <li>Your race (like where a company submits reports to the Department of Labour where the statistical information must be recorded).</li>
          <li>Your ethnic origin.</li>
          <li>Your trade union membership.</li>
          <li>Your health (like where you apply for an insurance policy).</li>
          <li>Your biometric information (like to verify your identity); and/or your criminal behaviour and alleged commission of an offence.</li>
        </ul>
      </section>

      <section>
        <h2>When Will We Process Your Special Personal Information?</h2>
        <p>
          We may process your special personal information in the following circumstances:
        </p>
        <ul className="policy-list">
          <li>If you have consented to the processing.</li>
          <li>If the information is being used for any Human Resource or payroll requirement.</li>
          <li>If the processing is needed to create, use, or protect a right or obligation in law.</li>
          <li>If the processing is for statistical or research purposes and all legal conditions are met.</li>
          <li>If the special personal information was made public by you.</li>
          <li>If the processing is required by law.</li>
          <li>If racial information is processed, and the processing is required to identify you; and/or if health information is processed, and the processing is to determine your insurance risk, or to comply with an insurance policy, or to enforce an insurance right or obligation.</li>
        </ul>
      </section>

      <section>
        <h2>When and From Where We Obtain Personal Information About You</h2>
        <p>
          We collect personal information from the following sources:
        </p>
        <ul className="policy-list">
          <li>The payroll or HR departments of our clients when they capture financial and non-financial information.</li>
          <li>3rd parties that are directly integrated with our software platform.</li>
          <li>Information about you based on your use of our products, services, or service channels.</li>
          <li>Information about you based on how you engage or interact with us, such as via our support desk, emails, letters, telephone calls, and surveys.</li>
        </ul>
        <p>
          If the law requires us to do so, we will ask for your consent before collecting personal information about you from third parties.
        </p>
        <p>
          The third parties from whom we may collect your personal information include, but are not limited to, the following:
        </p>
        <ul className="policy-list">
          <li>Partners of Lociware for any of the purposes identified in this Privacy Policy.</li>
          <li>Your spouse, dependents, partners, employer, and other similar sources.</li>
          <li>People you have authorised to share your personal information, like a person that makes a travel booking on your behalf or a medical practitioner for insurance purposes.</li>
          <li>Attorneys, tracing agents, debt collectors, and other persons that assist with the enforcement of agreements.</li>
          <li>Payment processing service providers, merchants, banks, and other persons that assist with the processing of your payment instructions, like EFT transaction partners.</li>
          <li>Insurers, brokers, other financial institutions, or other organisations that assist with insurance and assurance underwriting, the providing of insurance and assurance policies and products, the assessment of insurance and assurance claims, and other related purposes.</li>
          <li>Law enforcement and fraud prevention agencies and other persons tasked with the prevention and prosecution of crime.</li>
          <li>Regulatory authorities, industry ombudsman, governmental departments, local and international tax authorities.</li>
          <li>Trustees, Executors, or Curators appointed by a court of law.</li>
          <li>Our service providers, agents, and sub-contractors like couriers and other persons we use to offer and provide products and services to you.</li>
          <li>Courts of law or tribunals.</li>
          <li>Participating partners, whether retail or online, in our customer loyalty reward programmes.</li>
          <li>Our joint venture partners; and/or</li>
        </ul>
      </section>

      <section>
        <h2>Reasons We Need to Process Your Personal Information</h2>
        <p>
          We will process your personal information for the following reasons:
        </p>
        <ul className="policy-list">
          <li>To provide you with products, goods, and services.</li>
          <li>To market our products, goods, and services to you.</li>
          <li>To respond to your enquiries and complaints.</li>
          <li>To comply with legislative, regulatory, risk, and compliance requirements (including directives, sanctions, and rules), voluntary and involuntary codes of conduct, and industry agreements, or to fulfil reporting requirements and information requests.</li>
          <li>To conduct market and behavioural research, including scoring and analysis to determine if you qualify for products and services or to determine your credit or insurance risk.</li>
          <li>To develop, test, and improve products and services for you.</li>
          <li>For historical, statistical, and research purposes, like market segmentation.</li>
          <li>To process payment instruments.</li>
          <li>To create, manufacture, and print payment issues (like a payslip).</li>
          <li>To enable us to deliver goods, documents, or notices to you.</li>
          <li>For security, identity verification, and to check the accuracy of your personal information.</li>
          <li>To communicate with you and carry out your instructions and requests.</li>
          <li>For customer satisfaction surveys, promotional offerings.</li>
          <li>Insurance and assurance underwriting and administration.</li>
          <li>To process or consider or assess insurance or assurance claims.</li>
          <li>To provide insurance and assurance policies and products and related services.</li>
          <li>To enable you to take part in customer loyalty reward programmes, to determine your qualification for participation, earning of reward points, determining your rewards level, monitoring your buying behaviour with our rewards partners to allocate the correct points or inform you of appropriate products, goods, and services you may be interested in, or to inform our reward partners about your purchasing behaviour.</li>
          <li>To enable you to take part in and make use of value-added products and services.</li>
          <li>To assess our lending and insurance risks; and/or</li>
          <li>For any other related purposes.</li>
        </ul>
      </section>

      <section>
        <h2>How We Use Your Personal Information for Marketing</h2>
        <p>
          We will use your personal information to market financial, insurance, investments, and other related banking products and services to you.
        </p>
        <p>
          We may also market non-banking or non-financial products, goods, or services to you.
        </p>
        <p>
          We will do this in person, by post, telephone, or electronic channels such as SMS, email, and fax.
        </p>
        <p>
          If you are not our customer, or in any other instances where the law requires, we will only market to you by electronic communications with your consent.
        </p>
        <p>
          In all cases, you can request us to stop sending marketing communications to you at any time.
        </p>
      </section>

      <section>
        <h2>When, How, and With Whom We Share Your Personal Information</h2>
        <p>
          In general, we will only share your personal information if any one or more of the following apply:
        </p>
        <ul className="policy-list">
          <li>If you have consented to this.</li>
          <li>If it is necessary to conclude or perform under a contract we have with you.</li>
          <li>If the law requires it; and/or</li>
          <li>If it’s necessary to protect or pursue your, our, or a third party’s legitimate interest.</li>
        </ul>
      </section>

      <section>
        <h2>Under What Circumstances Will We Transfer Your Information to Other Countries?</h2>
        <p>
          We will only transfer your personal information to third parties in another country in any one or more of the following circumstances:
        </p>
        <ul className="policy-list">
          <li>Where your personal information will be adequately protected under the other country’s laws or an agreement with the third-party recipient.</li>
          <li>Where the transfer is necessary to enter into or perform under a contract with you, or a contract with a third party that is in your interest.</li>
          <li>Where you have consented to the transfer; and/or</li>
          <li>Where it is not reasonably practical to obtain your consent, the transfer is in your interest.</li>
        </ul>
        <p>
          This transfer will happen within the requirements and safeguards of the law. Where possible, the party processing your personal information in the other country will agree to apply the same level of protection as available by law in your country, or if the other country’s laws provide better protection, the other country’s laws would be agreed to and applied.
        </p>
        <p>
          An example of us transferring your personal information to another country is where foreign payments take place if you purchase goods or services in a foreign country.
        </p>
        <p>
          <strong>Take Note:</strong> We are a global organisation; your personal information may be shared within all Lociware entities in other countries and processed in those countries.
        </p>
      </section>

      <section>
        <h2>Your Duties and Rights About the Personal Information We Have About You</h2>
        <p>
          You must provide proof of identity when enforcing the rights below. You must inform us when your personal information changes.
        </p>
        <p>
          Please refer to our Promotion of Access to Information Act 2 of 2000 Manual (PAIA Manual) for further information on how you can give effect to the rights listed below. Download the PAIA Manual.
        </p>
        <p>
          You have the right to request access to the personal information we have about you by contacting us. This includes requesting:
        </p>
        <ul className="policy-list">
          <li>Confirmation that we hold your personal information.</li>
          <li>A copy or description of the record containing your personal information; and</li>
          <li>The identity or categories of third parties who have had access to your personal information.</li>
        </ul>
        <p>
          We will attend to requests for access to personal information within a reasonable time. You may be required to pay a reasonable fee to receive copies or descriptions of records, or information about third parties. We will inform you of the fee before attending to your request.
        </p>
        <p>
          Please note that the law may limit your right to access information.
        </p>
        <p>
          You have the right to request us to correct or delete the personal information we have about you if it is inaccurate, irrelevant, excessive, out of date, incomplete, misleading, obtained unlawfully, or we are no longer authorised to keep it. You must inform us of your request in writing. Please refer to our PAIA Manual for further information in this regard, like the process you should follow to give effect to this right. It may take up to 15 business days for the change to reflect on our systems. We may request documents from you to verify the change in personal information.
        </p>
        <p>
          A specific agreement that you have entered into with us may determine how you must change your personal information provided at the time when you entered into the specific agreement. Please adhere to these requirements. If the law requires us to keep the personal information, it will not be deleted upon your request. The deletion of certain personal information may lead to the termination of your business relationship with us.
        </p>
        <p>
          You may object on reasonable grounds to the processing of your personal information.
        </p>
        <p>
          We will not be able to give effect to your objection if the processing of your personal information was and is permitted by law; you have provided consent to the processing and our processing done according to your consent, or the processing is necessary to conclude or perform under a contract with you.
        </p>
        <p>
          Where you have provided your consent for the processing of your personal information, you may withdraw your consent. If you withdraw your consent, we will explain the consequences to you. We may proceed to process your personal information even if you have withdrawn your consent if the law permits or requires it. It may take up to 15 business days for the change to reflect on our systems; during this time, we may still process your personal information. You must inform us of any objection in writing. Please refer to our PAIA Manual for further information in this regard, like the process you should follow to give effect to this right.
        </p>
        <p>
          You have a right to file a complaint with us or any Regulator with jurisdiction about an alleged contravention of the protection of your personal information by us. We will address your complaint as far as possible.
        </p>
      </section>

      <section>
        <h2>How We Secure Your Personal Information</h2>
        <p>
          We will take appropriate and reasonable technical and organisational steps to protect your personal information according to industry best practices. Our security measures (including physical, technological, and procedural safeguards) will be appropriate and reasonable. This includes the following:
        </p>
        <ul className="policy-list">
          <li>Keeping our systems secure (like monitoring access and usage).</li>
          <li>Storing our records securely.</li>
          <li>Controlling the access to our buildings, systems, and/or records; and</li>
          <li>Safely destroying or deleting records.</li>
          <li>Ensuring compliance with international ISO security standards.</li>
        </ul>
        <p>
          You can also protect your personal information. Please visit the website of the relevant business you have established a business relationship with for more information.
        </p>
      </section>

      <section>
        <h2>How Long Do We Keep Your Personal Information?</h2>
        <p>
          We will keep your personal information for as long as:
        </p>
        <ul className="policy-list">
          <li>The law requires us to keep it.</li>
          <li>A contract between you and us requires us to keep it.</li>
          <li>You have consented for us keeping it.</li>
          <li>We are required to keep it to achieve the purposes listed in this Privacy Policy.</li>
          <li>We require it for statistical or research purposes.</li>
          <li>A code of conduct requires us to keep it; and/or</li>
          <li>We require it for our lawful business purposes.</li>
        </ul>
        <p>
          <strong>Take Note:</strong> We may keep your personal information even if you no longer have a relationship with us, for the historical data that may be required by your employer or employee.
        </p>
      </section>

      <section>
        <h2>Our Cookie Policy</h2>
        <p>
          A cookie is a small piece of data sent from our websites or applications to your computer or device hard drive or Internet browser where it is saved. The cookie contains information to personalise your experience on our websites or applications and may improve your experience on the websites or applications. The cookie will also identify your device, like the computer or smartphone.
        </p>
        <p>
          By using our websites or applications, you agree that cookies may be forwarded from the relevant website or application to your computer or device. The cookie will enable us to know that you have visited the website or application before and will identify you. We may also use the cookie to prevent fraud and for analytics.
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;